import React, { useState, useCallback, useRef } from 'react';
import { Box, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, NewPageTitleBar } from '@/components';
import FilterShopDateRange from '@/components/filters/FilterShopDateRange';
import { getMenuData } from '@core/data/menuData';
import AppCard from '@/components/appcard/AppCard';
import { hybridLocService } from '@/services/hybridLocService';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import WcnApexchart from '@/components/charts/WcnApexchart';
import { DateTime } from 'luxon';
import { FilterRef } from '@features/statistics/types.ts';
import { useNotifications } from '@core/hooks/useNotifications.ts';

interface FilterArgs {
    shopId: string;
    start: string;
    end: string;
}

const HybridLocDailyUsage: FCSecurePage = () => {
    const { t } = useTranslation();
    const [dataLoading, setDataLoading] = useState(false);
    const [series, setSeries] = useState<any[]>([]);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const filterRef = useRef<FilterRef>(null);
    const { notifyError } = useNotifications();

    const loadData = useCallback(
        async (args: FilterArgs) => {
            setDataLoading(true);
            setSeries([]);
            try {
                const data = await hybridLocService.loadDailyUsage(args.shopId, args.start, args.end);
                const seriesData = [
                    {
                        name: t('usage'),
                        data: data.map((item) => ({
                            x: item.date,
                            y: item.count
                        }))
                    }
                ];
                setSeries(seriesData);
                setLastUpdate(DateTime.now());
            } catch (error) {
                console.error('Failed to load HybridLoc daily usage data:', error);
                notifyError(t('errorLoadingData'));
            } finally {
                setDataLoading(false);
            }
        },
        [t]
    );

    const handleReload = useCallback(async () => {
        filterRef.current?.refresh();
    }, []);

    useRegisterRefresh(handleReload);

    return (
        <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default' }}>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-lock" />}
                title={`${t('hybridLoc')} ${t('dailyUsage')}`}
                iconSize="18px"
                menus={getMenuData()}
                currentRoute={{ path: '/statistics/hybridloc/daily-usage' }}
                lastUpdate={lastUpdate}
                t={t}
            />
            <Container maxWidth={false}>
                <FilterShopDateRange ref={filterRef} range={0} maxRange={Number.MAX_VALUE} onLoadData={loadData} />
                {dataLoading ? (
                    <LoadingSpinner showBackground={false} />
                ) : (
                    <AppCard
                        heading={`${t('hybridLoc')} ${t('dailyUsage')}`}
                        icon="lock"
                        colClasses="flex col-12"
                        customClasses="mb-0"
                        fullBlock={false}
                        footer={false}
                    >
                        <WcnApexchart
                            chartId="hl-daily-usage-chart"
                            type="line"
                            series={series}
                            height={550}
                            xAxisType="datetime"
                            yAxisMin={0}
                            strokeCurve="straight"
                            strokeDashArray={[0]}
                            tooltipShared={false}
                        />
                    </AppCard>
                )}
            </Container>
        </Box>
    );
};

export default HybridLocDailyUsage;
